import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Styled from 'styled';
import { Link } from 'react-router-dom';

import picture from 'four-nets/icons/svg/icon_picture.svg';
import clock from 'four-nets/icons/svg/icon_activity2.svg';
import userIcon from 'four-nets/icons/svg/icon_user.svg';
import tag from 'four-nets/icons/svg/icon_tag.svg';
import users from 'four-nets/icons/svg/icon_users.svg';

import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';
import ResponsiveImageView from 'lib/images/responsive-image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { ProductFragment } from './product.graphql';
import defaultStyles from '../product-common.scss';

export interface Props extends ProductFragment {
    styles: typeof defaultStyles;
    excludeUser?: boolean;
}

const Product: React.FC<Props> = (props, context) => {
    const { site } = context;
    const {
        styles,
        title,
        description,
        user,
        buyersCount,
        url,
        price,
        startTime,
        isVendor,
        featuredImage,
        excludeUser,
    } = props;
    const username = user && user.username;

    return (
        <div className={classnames(styles.tile, { [styles.used]: !isVendor })}>
            <div className={classnames(styles.image, { [styles.used]: !isVendor })}>
                <Link to={url || ''}>
                    {featuredImage ? (
                        <FormattedMessage id='IMAGE'>
                            {msg => (
                                <ResponsiveImageView
                                    alt={`${title} - ${msg} č. 1`}
                                    aspectFill={true}
                                    image={featuredImage}
                                    sizes={{ xs: '90vw', md: 250 }}
                                    linkify={false}
                                />
                            )}
                        </FormattedMessage>
                    ) : (
                        <div className={styles.noPhoto}>
                            <SVGIcon style={{ image: styles.icon }} src={picture} />
                        </div>
                    )}
                </Link>
            </div>
            <div className={styles.contentWrapper}>
                <div className={styles.dateAndBadgeDiv}>
                    <div className={styles.timeDiv}>
                        {startTime ? (
                            <div className={styles.date}>
                                <SVGIcon style={{ image: styles.icon }} src={clock} />
                                <FormattedDateAgoView value={new Date(startTime)} />
                            </div>
                        ) : null}
                    </div>
                    {site && !site.isGarden ? (
                        <div className={classnames(styles.newLabel, { [styles.usedLabel]: !isVendor })}>
                            {isVendor ? (
                                <FormattedMessage id='NEW_GOODS' />
                            ) : (
                                <FormattedMessage id='PRODUCTS_FROM_BAZAAR' />
                            )}
                        </div>
                    ) : (
                        <div className={classnames(styles.newLabel, { [styles.usedLabel]: !isVendor })}>
                            {isVendor ? (
                                <FormattedMessage id='NEW_GOODS' />
                            ) : (
                                <FormattedMessage id='PRODUCTS_FROM_SECOND_HAND' />
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.mainLinkWrapper}>
                    <Link to={url || ''} className={styles.mainLink}>
                        {title}
                    </Link>
                </div>
                <div className={styles.sellingInfoDiv}>
                    <div className={styles.price}>
                        <div className={styles.priceContent}>
                            <SVGIcon style={{ image: styles.icon }} src={tag} />
                            {price && price > 0 ? (
                                `${price} ${site && site.currency}`
                            ) : (
                                <FormattedMessage id='GIVE_FOR_FREE' />
                            )}
                        </div>
                    </div>
                    {buyersCount ? (
                        <div className={styles.buyersWrapper}>
                            <SVGIcon style={{ image: styles.icon }} src={users} />
                            <span className={styles.container}>{buyersCount}</span>
                        </div>
                    ) : (
                        <span />
                    )}
                </div>
            </div>
            {excludeUser ? null : (
                <div className={styles.user}>
                    <div className={styles.userInfoDiv}>
                        <div className={styles.userWrapper}>
                            <SVGIcon style={{ image: styles.userIcon }} src={userIcon} />
                            <Link to={url || ''} className={styles.link}>
                                {username}
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

Product.contextTypes = {
    site: PropTypes.object,
};

export default Styled(defaultStyles)(Product);
